import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { getUrlParamsFetch } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  CitiesDataPagingRes,
  CityParams,
  ProvinceParams,
  ProvincesDataPagingRes,
  UseFetchEnabledParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { GetSectorsParams } from '../sectors.actions';
import { Cobertura, CoberturasPagingRes, ZoneParams, ZonesPagingRes, SectorsPagingRes, Zone } from '@/shared/interfaces/erp/mantenimiento-operaciones';

const { get, post, put, remove } = erpApi();

///* tanSctack query
export const useFetchCoberturas = ({
  page = 1,
  provincia = '',
  ciudad = '',
  sector = '' as any,
  page_size = 10,
}: GetCoberurasParams) => {
  return useQuery({
    queryKey: ['coberturas', page, provincia, ciudad, sector],
    queryFn: async () => {
      return getCoberturas({ page, provincia, ciudad, sector, page_size });
    },
  });
};

export const useGetCobertura = (id: string) => {
  return useQuery({
    queryKey: ['coberturas', id],
    queryFn: async () => {
      return getCobertura(id);
    },
  });
};

export const useCreateCobertura = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCobertura,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coberturas'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

export const useUpdateCobertura = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCobertura,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coberturas'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

export const useRemoveCobertura = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: deleteCobertura,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coberturas'] });
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
  });
};

///* axios
export type GetCoberurasParams = {
  page?: number;
  page_size?: number;

  // search - fk:
  provincia?: string; // name
  ciudad?: string; // name
  sector?: number; // id
};

export type CreateCoberturaParams = Omit<Cobertura, 'id_cobertura'>;
export type UpdateCoberturaParams = {
  id: string;
  data: CreateCoberturaParams;
};

// api/coberturas/?provincia=a&ciudad=a&sector=12

export const getCoberturas = async (params?: GetCoberurasParams) => {
  const {
    page = 1,
    page_size = 10,
    provincia = '',
    ciudad = '',
    sector = '',
  } = params || {};

  return get<CoberturasPagingRes>(
    `${urlBackend}api/v1/coberturas/?page=${page}&page_size=${page_size}&provincia=${provincia}&ciudad=${ciudad}&sector=${sector}`,
    true
  );
};

export const getCobertura = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<Cobertura>(
    `${urlBackend}api/v1/coberturas/${id}/`,
    true
  );

  res.status === 'error' && setAlert(true, res.message, res.status);
  return res;
};

export const createCobertura = async (data: CreateCoberturaParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<Cobertura>(
    `${urlBackend}api/v1/coberturas/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateCobertura = async ({ id, data }: UpdateCoberturaParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<Cobertura>(
    `${urlBackend}api/v1/coberturas/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const deleteCobertura = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<Cobertura>(
    `${urlBackend}api/v1/coberturas/${id}/`,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

//OBTENER COBERTURA PROVINCIAS

export const useFetchCoberturasProvincia = (
  params?: ProvinceParams,
  enabled = true
) => {
  return useQuery({
    queryKey: ['coberturas', ...Object.values(params || {})],
    queryFn: async () => {
      return getCoberturaProvincia(params);
    },
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const getCoberturaProvincia = async (params?: ProvinceParams) => {
  const queryParams = getUrlParamsFetch(
    {
      cobertura: true,
      estado: true,
      ...params,
    }
  );

  const resp = await get<ProvincesDataPagingRes>(
    `${urlBackend}api/v1/provincia/?${queryParams}`,
    true
  );

  return resp.data;
};

//OBTENER COBERTURA CIUADES

export const useFetchCoberturasCiudad = (params?: CityParams) => {
  return useQuery({
    queryKey: ['coberturas', ...Object.values(params || {})],
    queryFn: async () => {
      return getCoberturaCiudad(params);
    },
  });
};

export const getCoberturaCiudad = async (params?: CityParams) => {
  const queryClient = getUrlParamsFetch(params || {});
  const res = await get<CitiesDataPagingRes>(
    `${urlBackend}api/v1/ciudad/?${queryClient}`,
    true
  );
  return res.data;
};

//OBTENER COBERTURA ZONA

export const useFetchCoberturasZona = (params?: ZoneParams) => {
  return useQuery({
    queryKey: ['coberturas', ...Object.values(params || {})],
    queryFn: async () => {
      return getCoberturaZona(params);
    },
  });
};

export const getCoberturaZona = async (params?: ZoneParams) => {
  const queryParams = getUrlParamsFetch(params || {});
  const res = await get<ZonesPagingRes>(
    `${urlBackend}api/v1/zona/?${queryParams}`,
    true
  );
  return res.data;
};

//OBETENER COBERTURA SECTOR

export const useFetchCoberturasSector = (params?: GetSectorsParams, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['coberturas', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getCoberturaSector(params);
    },
    retry: false,
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const getCoberturaSector = async (params?: GetSectorsParams) => {
  const queryParams = getUrlParamsFetch(params ?? {});
  const res = await get<SectorsPagingRes>(
    `${urlBackend}api/v1/sector/?${queryParams}`,
    true
  );
  return res.data;
};

///// ============  ENABLED ============
///* tanSctack query
export const useFetchCoberturasCiudadEnabled = ({
  enabled,
  params,
}: UseFetchEnabledParams<GetCiudadCoberturaParams>) => {
  return useQuery({
    queryKey: ['coberturas-ciudades', params, enabled],
    queryFn: async () => {
      return getCiudadesConCobertura(params);
    },
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const useFetchCoberturasZonaEnabled = ({
  enabled,
  params,
}: UseFetchEnabledParams<GetZonasCoberturaParams>) => {
  return useQuery({
    queryKey: ['coberturas-zonas', params, enabled],
    queryFn: async () => {
      return getZonasConCobertura(params);
    },
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const useFetchCoberturasSectorEnabled = ({
  enabled,
  params,
}: UseFetchEnabledParams<GetSectoresCoberturaParams>) => {
  return useQuery({
    queryKey: ['coberturas-sectores', params, enabled],
    queryFn: async () => {
      return getSectoresConCobertura(params);
    },
    enabled,
    refetchOnWindowFocus: false,
  });
};

///* axios
export type GetCiudadCoberturaParams = {
  page?: number;
  page_size?: number;
  nombre?: string;
  provincia?: string;
  cobertura?: boolean;
  estado?: boolean
};
export type GetZonasCoberturaParams = Partial<Zone> & {
  page?: number;
  page_size?: number;
  nombre?: string;
  ciudad?: string;
};
export type GetSectoresCoberturaParams = {
  page?: number;
  page_size?: number;
  nombre?: string;
  ciudad?: string;
  zona?: string;
};

export const getCiudadesConCobertura = async (
  params?: GetCiudadCoberturaParams
) => {
  const queryParams = getUrlParamsFetch(
    {
      cobertura: true,
      estado: true,
      ...params,
    }
  );

  return get<CitiesDataPagingRes>(
    `${urlBackend}api/v1/ciudad/?${queryParams}`,
    true
  );
};

export const getZonasConCobertura = async (
  params?: GetZonasCoberturaParams
) => {
  const queryParams = getUrlParamsFetch(
    {
      ...params,
      cobertura: true,
      estado: true,
    }
  );

  return get<ZonesPagingRes>(`${urlBackend}api/v1/zona/?${queryParams}`, true);
};

export const getSectoresConCobertura = async (
  params?: GetSectoresCoberturaParams
) => {
  const queryParams = getUrlParamsFetch(
    { ...params, cobertura: true, estado: true }
  );

  return get<SectorsPagingRes>(
    `${urlBackend}api/v1/sector/?${queryParams}`,
    true
  );
};